import * as React from 'react';

import { useLocation, useSearchParams, type SetURLSearchParams } from 'react-router-dom';

import type { SalesTabKey } from '@/components/analytics/sales/SalesTabPills';
import { usePhone } from '@/hooks/usePhone';
import { recoverFromChunkLoadError } from '@/lib/chunk-load-recovery';

import { DefaultTopbarSearch } from './DefaultTopbarSearch';
import { StockTitledTopbarContent } from './StockTitledTopbar';
import { resolveTopbarKind, type TopbarKind } from './topbarRoute';
import { useGoposOrdersTopbarState } from './useGoposOrdersTopbarState';
import { useInvoicesListTopbarState } from './useInvoicesListTopbarState';
import { useInvoiceDetailTopbarState, useInventoryDetailTopbarState } from './useTopbarDetailState';

type TopbarContentComponent = () => React.ReactNode;

interface SearchParamsRouteProps {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

function suspendUntilReload(): Promise<never> {
  return new Promise(() => {
    // A successful chunk recovery reload replaces this runtime.
  });
}

function lazyTopbarComponent<Props>(
  load: () => Promise<React.ComponentType<Props>>,
): React.LazyExoticComponent<React.ComponentType<Props>> {
  return React.lazy(async () => {
    try {
      return { default: await load() };
    } catch (error) {
      if (recoverFromChunkLoadError(error, 'lazy:import')) {
        return suspendUntilReload();
      }
      throw error;
    }
  });
}

const SalesTabPillsLazy = lazyTopbarComponent(() =>
  import('@/components/analytics/sales/SalesTabPills').then((module) => module.SalesTabPills),
);
const AnalyticsPeriodBadge = lazyTopbarComponent(() =>
  import('./AnalyticsTopbar').then((module) => module.AnalyticsPeriodBadge),
);
const AnalyticsTopbarActions = lazyTopbarComponent(() =>
  import('./AnalyticsTopbar').then((module) => module.AnalyticsTopbarActions),
);
const AnalyticsTopbarContext = lazyTopbarComponent(() =>
  import('./AnalyticsTopbar').then((module) => module.AnalyticsTopbarContext),
);
const SalesProductsCategorySelect = lazyTopbarComponent(() =>
  import('./AnalyticsTopbar').then((module) => module.SalesProductsCategorySelect),
);
const B2BCustomersTopbarActions = lazyTopbarComponent(() =>
  import('./B2BCustomersTopbar').then((module) => module.B2BCustomersTopbarActions),
);
const B2BCustomersTopbarSearch = lazyTopbarComponent(() =>
  import('./B2BCustomersTopbar').then((module) => module.B2BCustomersTopbarSearch),
);
const B2BSalesTopbarActions = lazyTopbarComponent(() =>
  import('./B2BSalesTopbar').then((module) => module.B2BSalesTopbarActions),
);
const B2BSalesTopbarSearch = lazyTopbarComponent(() =>
  import('./B2BSalesTopbar').then((module) => module.B2BSalesTopbarSearch),
);
const BalancesTopbarContent = lazyTopbarComponent(() =>
  import('./BalancesTopbar').then((module) => module.BalancesTopbarContent),
);
const BankTopbarContent = lazyTopbarComponent(() =>
  import('./BankTopbarContent').then((module) => module.BankTopbarContent),
);
const CategoriesTopbarActions = lazyTopbarComponent(() =>
  import('./CategoriesTopbar').then((module) => module.CategoriesTopbarActions),
);
const CategoriesTopbarSearch = lazyTopbarComponent(() =>
  import('./CategoriesTopbar').then((module) => module.CategoriesTopbarSearch),
);
const GoposOrdersTopbarActions = lazyTopbarComponent(() =>
  import('./GoposOrdersTopbar').then((module) => module.GoposOrdersTopbarActions),
);
const GoposOrdersTopbarSearch = lazyTopbarComponent(() =>
  import('./GoposOrdersTopbar').then((module) => module.GoposOrdersTopbarSearch),
);
const IngredientsTopbarActions = lazyTopbarComponent(() =>
  import('./IngredientsTopbar').then((module) => module.IngredientsTopbarActions),
);
const IngredientsTopbarSearch = lazyTopbarComponent(() =>
  import('./IngredientsTopbar').then((module) => module.IngredientsTopbarSearch),
);
const InventoriesTopbarContent = lazyTopbarComponent(() =>
  import('./InventoriesTopbar').then((module) => module.InventoriesTopbarContent),
);
const InventoryDetailTopbar = lazyTopbarComponent(() =>
  import('./InventoryDetailTopbar').then((module) => module.InventoryDetailTopbar),
);
const InvoiceDetailTopbar = lazyTopbarComponent(() =>
  import('./InvoiceDetailTopbar').then((module) => module.InvoiceDetailTopbar),
);
const InvoicesListTopbarActions = lazyTopbarComponent(() =>
  import('./InvoicesListTopbar').then((module) => module.InvoicesListTopbarActions),
);
const InvoicesListTopbarSearch = lazyTopbarComponent(() =>
  import('./InvoicesListTopbar').then((module) => module.InvoicesListTopbarSearch),
);
const MoneyTabs = lazyTopbarComponent(() =>
  import('@/components/finance/MoneyTabs').then((module) => module.MoneyTabs),
);
const PaymentsTopbarLeading = lazyTopbarComponent(() =>
  import('./PaymentsTopbar').then((module) => module.PaymentsTopbarLeading),
);
const PaymentsTopbarTrailing = lazyTopbarComponent(() =>
  import('./PaymentsTopbar').then((module) => module.PaymentsTopbarTrailing),
);
const ProductionsTopbarActions = lazyTopbarComponent(() =>
  import('./ProductionsTopbar').then((module) => module.ProductionsTopbarActions),
);
const ProductionsTopbarSearch = lazyTopbarComponent(() =>
  import('./ProductionsTopbar').then((module) => module.ProductionsTopbarSearch),
);
const ProductsTopbarContent = lazyTopbarComponent(() =>
  import('./ProductsTopbar').then((module) => module.ProductsTopbarContent),
);
const ReceiptsTopbarActions = lazyTopbarComponent(() =>
  import('./ReceiptsTopbar').then((module) => module.ReceiptsTopbarActions),
);
const ReceiptsTopbarSearch = lazyTopbarComponent(() =>
  import('./ReceiptsTopbar').then((module) => module.ReceiptsTopbarSearch),
);
const RecipesTopbarActions = lazyTopbarComponent(() =>
  import('./RecipesTopbar').then((module) => module.RecipesTopbarActions),
);
const RecipesTopbarSearch = lazyTopbarComponent(() =>
  import('./RecipesTopbar').then((module) => module.RecipesTopbarSearch),
);
const ExpiryRiskTopbarContent = lazyTopbarComponent(() =>
  import('./ExpiryRiskTopbar').then((module) => module.ExpiryRiskTopbarContent),
);
const SellerWorkspaceTopbarContent = lazyTopbarComponent(() =>
  import('./SellerWorkspaceTopbar').then((module) => module.SellerWorkspaceTopbarContent),
);
const StockOperationsTopbarActions = lazyTopbarComponent(() =>
  import('./StockOperationsTopbar').then((module) => module.StockOperationsTopbarActions),
);
const StockOperationsTopbarContent = lazyTopbarComponent(() =>
  import('./StockOperationsTopbar').then((module) => module.StockOperationsTopbarContent),
);
const SupplierReturnsTopbarActions = lazyTopbarComponent(() =>
  import('./SupplierReturnsTopbar').then((module) => module.SupplierReturnsTopbarActions),
);
const SupplierReturnsTopbarSearch = lazyTopbarComponent(() =>
  import('./SupplierReturnsTopbar').then((module) => module.SupplierReturnsTopbarSearch),
);
const TransfersTopbarActions = lazyTopbarComponent(() =>
  import('./TransfersTopbar').then((module) => module.TransfersTopbarActions),
);
const TransfersTopbarSearch = lazyTopbarComponent(() =>
  import('./TransfersTopbar').then((module) => module.TransfersTopbarSearch),
);
const WriteOffsTopbarActions = lazyTopbarComponent(() =>
  import('./WriteOffsTopbar').then((module) => module.WriteOffsTopbarActions),
);
const WriteOffsTopbarSearch = lazyTopbarComponent(() =>
  import('./WriteOffsTopbar').then((module) => module.WriteOffsTopbarSearch),
);

function TopbarSplitContent({
  leading,
  trailing,
  showSpacer = true,
}: {
  leading: React.ReactNode;
  trailing?: React.ReactNode;
  showSpacer?: boolean;
}) {
  return (
    <>
      {leading}
      {showSpacer ? <div className="flex-1" /> : null}
      {trailing ?? null}
    </>
  );
}

function useTopbarSearchParams(): SearchParamsRouteProps {
  const [searchParams, setSearchParams] = useSearchParams();
  return { searchParams, setSearchParams };
}

function DefaultTopbarContent() {
  return <DefaultTopbarSearch />;
}

function EmptyTopbarContent() {
  return null;
}

function InvoiceDetailTopbarContent() {
  const { invoice, invoiceId, handleInvoiceBack } = useInvoiceDetailTopbarState();
  return <InvoiceDetailTopbar invoice={invoice} invoiceId={invoiceId} onBack={handleInvoiceBack} />;
}

function InventoryDetailTopbarContent() {
  const { inventory, searchInput, handleSearchChange, handleBack } =
    useInventoryDetailTopbarState();
  return (
    <InventoryDetailTopbar
      inventory={inventory}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onBack={handleBack}
    />
  );
}

function InvoicesListTopbarContent() {
  const { searchInput, handleSearchChange, groupBy, setGroupBy } = useInvoicesListTopbarState();
  return (
    <TopbarSplitContent
      leading={<InvoicesListTopbarSearch value={searchInput} onChange={handleSearchChange} />}
      trailing={<InvoicesListTopbarActions groupBy={groupBy} onGroupByChange={setGroupBy} />}
    />
  );
}

function GoposOrdersRouteContent() {
  const phone = usePhone();
  const {
    filters,
    venueOptions,
    isLoadingVenues,
    searchInput,
    handleSearchChange,
    handleDateRangeChange,
    handleVenueChange,
    handleResetAllFilters,
  } = useGoposOrdersTopbarState();

  return (
    <TopbarSplitContent
      leading={
        phone ? (
          // The search field sits at the top of the page on a phone (OrdersPage).
          <span className="truncate text-sm font-semibold text-foreground">Журнал продаж</span>
        ) : (
          <GoposOrdersTopbarSearch value={searchInput} onChange={handleSearchChange} />
        )
      }
      trailing={
        <GoposOrdersTopbarActions
          filters={filters}
          venueOptions={venueOptions}
          isLoadingVenues={isLoadingVenues}
          onVenueChange={handleVenueChange}
          onDateRangeChange={handleDateRangeChange}
          onResetAllFilters={handleResetAllFilters}
        />
      }
    />
  );
}

function ProductCategoriesRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <CategoriesTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <CategoriesTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function ProductsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return <ProductsTopbarContent searchParams={searchParams} setSearchParams={setSearchParams} />;
}

function IngredientsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <IngredientsTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <IngredientsTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function WriteOffsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <WriteOffsTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <WriteOffsTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function TransfersRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <TransfersTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <TransfersTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function ReceiptsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <ReceiptsTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <ReceiptsTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function SupplierReturnsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <SupplierReturnsTopbarSearch
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      }
      trailing={
        <SupplierReturnsTopbarActions
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      }
    />
  );
}

function ProductionsRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <ProductionsTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <ProductionsTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function RecipesRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <RecipesTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <RecipesTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function B2BSalesRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <B2BSalesTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <B2BSalesTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function InventoriesRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return <InventoriesTopbarContent searchParams={searchParams} setSearchParams={setSearchParams} />;
}

function BalancesRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return <BalancesTopbarContent searchParams={searchParams} setSearchParams={setSearchParams} />;
}

function StockOperationsRouteContent() {
  const phone = usePhone();
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        phone ? (
          // Search and filters sit at the top of the page on a phone (OperationsPage).
          <span className="truncate text-sm font-semibold text-foreground">Операции</span>
        ) : (
          <StockOperationsTopbarContent
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )
      }
      trailing={
        <StockOperationsTopbarActions
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      }
    />
  );
}

function B2BCustomersRouteContent() {
  const { searchParams, setSearchParams } = useTopbarSearchParams();
  return (
    <TopbarSplitContent
      leading={
        <B2BCustomersTopbarSearch searchParams={searchParams} setSearchParams={setSearchParams} />
      }
      trailing={
        <B2BCustomersTopbarActions searchParams={searchParams} setSearchParams={setSearchParams} />
      }
    />
  );
}

function PaymentsRouteContent() {
  return (
    <TopbarSplitContent leading={<PaymentsTopbarLeading />} trailing={<PaymentsTopbarTrailing />} />
  );
}

function BankRouteContent() {
  return (
    <TopbarSplitContent leading={<MoneyTabs />} trailing={<BankTopbarContent />} showSpacer={false} />
  );
}

function MoneyRouteContent() {
  return <TopbarSplitContent leading={<MoneyTabs />} />;
}

function SellerWorkspaceRouteContent() {
  return <SellerWorkspaceTopbarContent />;
}

function ExpiryRiskRouteContent() {
  return <ExpiryRiskTopbarContent />;
}

function AnalyticsSalesRouteContent() {
  const phone = usePhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: SalesTabKey = (
    ['overview', 'products', 'locations', 'b2b', 'time'] as const
  ).includes(requestedTab as never)
    ? (requestedTab as SalesTabKey)
    : 'overview';

  const categoryId = searchParams.get('category') ?? 'all';

  const setTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (tab !== 'overview' && tab !== 'products') {
      next.delete('category');
    }
    setSearchParams(next, { replace: true });
  };

  const setCategoryId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.delete('category');
    } else {
      next.set('category', id);
    }
    setSearchParams(next, { replace: true });
  };

  if (phone) {
    // Tabs and category live in the page on a phone (SalesPhoneBar).
    return (
      <TopbarSplitContent
        leading={<span className="truncate text-sm font-semibold text-foreground">Продажи</span>}
        trailing={<AnalyticsTopbarActions />}
      />
    );
  }

  return (
    <>
      <SalesTabPillsLazy activeTab={activeTab} onTabChange={setTab} />
      <div className="flex flex-1 justify-center">
        <AnalyticsPeriodBadge />
      </div>
      <div className="flex items-center gap-2">
        {(activeTab === 'overview' || activeTab === 'products') && (
          <SalesProductsCategorySelect categoryId={categoryId} onCategoryChange={setCategoryId} />
        )}
        <AnalyticsTopbarActions />
      </div>
    </>
  );
}

function AnalyticsRouteContent() {
  const location = useLocation();
  return (
    <TopbarSplitContent
      leading={<AnalyticsTopbarContext pathname={location.pathname} />}
      trailing={<AnalyticsTopbarActions />}
    />
  );
}

const topbarRegistry: Record<TopbarKind, TopbarContentComponent> = {
  default: DefaultTopbarContent,
  empty: EmptyTopbarContent,
  invoice_detail: InvoiceDetailTopbarContent,
  inventory_detail: InventoryDetailTopbarContent,
  invoices_list: InvoicesListTopbarContent,
  gopos_orders: GoposOrdersRouteContent,
  product_categories: ProductCategoriesRouteContent,
  products: ProductsRouteContent,
  ingredients: IngredientsRouteContent,
  write_offs: WriteOffsRouteContent,
  transfers: TransfersRouteContent,
  receipts: ReceiptsRouteContent,
  supplier_returns: SupplierReturnsRouteContent,
  productions: ProductionsRouteContent,
  recipes: RecipesRouteContent,
  b2b_sales: B2BSalesRouteContent,
  inventories: InventoriesRouteContent,
  balances: BalancesRouteContent,
  stock_operations: StockOperationsRouteContent,
  stock_titled: StockTitledTopbarContent,
  seller_workspace: SellerWorkspaceRouteContent,
  expiry_risk: ExpiryRiskRouteContent,
  b2b_customers: B2BCustomersRouteContent,
  bank: BankRouteContent,
  payments: PaymentsRouteContent,
  money: MoneyRouteContent,
  analytics_sales: AnalyticsSalesRouteContent,
  analytics: AnalyticsRouteContent,
};

export function TopbarLoadingFallback() {
  return (
    <div
      aria-hidden="true"
      className="h-8 w-72 max-w-full shrink-0 animate-pulse rounded-md bg-muted"
      data-testid="topbar-loading-fallback"
    />
  );
}

export function TopbarRouteContent() {
  const location = useLocation();
  const kind = React.useMemo(() => resolveTopbarKind(location.pathname), [location.pathname]);
  const Content = topbarRegistry[kind];
  return (
    <React.Suspense fallback={<TopbarLoadingFallback />}>
      <Content />
    </React.Suspense>
  );
}
